import React from "react";
import LegalLayout from "./_LegalLayout";

const H = ({ children }) => (
  <h2 className="font-display text-xl sm:text-2xl font-semibold text-ink-900 tracking-tight mt-8">
    {children}
  </h2>
);

const Privacy = () => (
  <LegalLayout
    testId="page-privacy"
    overline="Legal · Privacy Policy"
    title="Privacy Policy"
    lead="How Allinstra handles personal data through this website."
  >
    <p className="text-sm text-ink-500">Effective date: 16 April 2026 · Last updated: 16 April 2026</p>

    <p>
      Allinstra is an institutional systems brand by Erenown. This website is proposed to be
      operated primarily by Erenown LLC, a company registered in the Republic of Kosovo. In
      certain commercial or operational contexts, specific services may instead be provided by
      Erenown Ltd, a company registered in England and Wales, where that is expressly stated
      in the relevant communication, proposal, engagement letter, or contract.
    </p>
    <p>
      Erenown Ltd and Erenown LLC are separate independent companies under the same ultimate
      beneficial ownership and control. Nothing in this Privacy Policy should be read as
      implying that one company is a branch, subsidiary, affiliate, or office of the other
      unless expressly stated elsewhere in a signed legal instrument.
    </p>

    <H>1. Who controls personal data?</H>
    <p>
      For general website operation, inquiries, and inbound lead handling, the primary data
      controller is proposed to be:
    </p>
    <div className="border border-ink-200 bg-ink-50 p-5">
      <div className="font-semibold text-ink-900">Erenown LLC</div>
      <div>Bulevardi Dëshmorët e Kombit, C2, HIII, Nr.6</div>
      <div>Prishtinë, Republic of Kosovo</div>
      <div>Registration number / unique identification number: 811869512</div>
    </div>
    <p>
      Where a request is handled directly by the UK company, or where a service, agreement, or
      follow-up is explicitly transferred to the UK company, the relevant controller may
      instead be:
    </p>
    <div className="border border-ink-200 bg-ink-50 p-5">
      <div className="font-semibold text-ink-900">Erenown Ltd</div>
      <div>83 St. Marks Road, Enfield, England, EN1 1BJ</div>
      <div>United Kingdom</div>
      <div>Company number: 12845592</div>
    </div>
    <p>
      General contact email for privacy and website matters:{" "}
      <a href="mailto:info@allinstra.com" className="underline">info@allinstra.com</a>
    </p>

    <H>2. What information we collect</H>
    <p>We may collect:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>identity and contact details such as full name, company name, country, email address, phone number, and message details;</li>
      <li>business inquiry details such as institutional system interest, product or module interest, implementation needs, integration interest, market interest, or collaboration interest;</li>
      <li>message content, attachments, technical inquiry details, and any information you choose to submit;</li>
      <li>website usage data such as browser type, device information, IP-related technical logs, cookie preferences, and analytics data where enabled.</li>
    </ul>

    <H>3. How we collect information</H>
    <p>We collect information:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>when you complete a website form;</li>
      <li>when you request contact or send us an email;</li>
      <li>when you communicate with us about services, products, implementations, localization, integrations, or business opportunities;</li>
      <li>through cookies and similar technologies, subject to your preferences where required.</li>
    </ul>

    <H>4. Why we use personal data</H>
    <p>We use personal data:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>to respond to inquiries and assess whether Allinstra or Erenown can assist you;</li>
      <li>to arrange follow-up communications, proposals, demonstrations, and implementation discussions;</li>
      <li>to manage inbound leads, institutional contacts, and business communications;</li>
      <li>to improve website performance, security, usability, and content;</li>
      <li>to keep business records and maintain communication history;</li>
      <li>to send relevant updates where permitted by law or where you have opted in.</li>
    </ul>

    <H>5. Legal bases</H>
    <p>Depending on the context and applicable law, we may rely on:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>pre-contractual steps, where you ask us to discuss or assess a possible service;</li>
      <li>legitimate interests, for the structured operation, improvement, and protection of our website and communications;</li>
      <li>consent, where required for non-essential cookies or optional marketing communications;</li>
      <li>legal obligations, where retention or disclosure is required by applicable law.</li>
    </ul>

    <H>6. Sharing of data</H>
    <p>
      Personal data may be shared on a need-to-know basis with website hosting providers,
      analytics providers, email tools, CRM tools, professional advisers, and selected
      technical or business service providers where relevant to your request.
    </p>
    <p>
      Data may also be shared between Erenown LLC and Erenown Ltd where necessary to assess,
      route, or perform a specific business opportunity, provided that the receiving company
      is clearly identified when appropriate.
    </p>
    <p>We do not sell personal data.</p>

    <H>7. International transfers</H>
    <p>
      Because Allinstra is intended to support coordination across selected international
      markets, personal data may be accessed or processed across more than one jurisdiction.
      Where that occurs, we aim to apply appropriate contractual, organisational, and technical
      safeguards proportionate to the circumstances.
    </p>

    <H>8. Retention</H>
    <p>
      We keep personal data only for as long as reasonably necessary to respond to your
      request, maintain business records, manage follow-up communications, comply with legal
      obligations, resolve disputes, and protect legitimate business interests.
    </p>

    <H>9. Your rights</H>
    <p>Depending on applicable law, you may have rights to:</p>
    <ul className="list-disc pl-5 space-y-1.5">
      <li>access your personal data;</li>
      <li>request correction or update of inaccurate data;</li>
      <li>request deletion where retention is no longer necessary;</li>
      <li>object to certain processing;</li>
      <li>withdraw consent where consent is the legal basis;</li>
      <li>ask for restriction or portability where applicable;</li>
      <li>opt out of marketing communications at any time.</li>
    </ul>
    <p>
      To exercise a privacy right, contact:{" "}
      <a href="mailto:info@allinstra.com" className="underline">info@allinstra.com</a>
    </p>

    <H>10. Cookies</H>
    <p>
      Please read the Allinstra <a href="/cookie-policy" className="underline">Cookie Policy</a>{" "}
      and use the site cookie preference controls for more information.
    </p>

    <H>11. Security</H>
    <p>
      We apply reasonable technical and organisational safeguards to protect personal data.
      However, no website, communication channel, or storage system can be guaranteed to be
      completely secure.
    </p>

    <H>12. Third-party websites</H>
    <p>
      Where our website links to third-party partners, public authorities, or external
      information sources, their privacy practices are governed by their own policies and
      notices.
    </p>

    <H>13. Change of operating entity or transfer of website operations</H>
    <p>
      Website operations, records, communications, and related rights and obligations may in
      the future be transferred, assigned, or novated to Erenown Ltd or to another company
      under the same ultimate beneficial ownership and control, where commercially appropriate
      and legally permitted. If that occurs, this Privacy Policy will be updated and the
      relevant operating entity will be clearly identified.
    </p>

    <H>14. Contact</H>
    <p>
      For privacy questions or complaints, contact:{" "}
      <a href="mailto:info@allinstra.com" className="underline">info@allinstra.com</a>
    </p>
  </LegalLayout>
);

export default Privacy;
